import { activityShop, type PulsePlace, type PulseShop } from '#lib/footfall/pulse';
import type { FootfallDay, PropertyVenue, PulseMapActivity, PulseMove, PulseTrafficView } from '#types/types';

export const DEFAULT_TRAFFIC_VIEW: PulseTrafficView = { mode: 'activity', day: 'sat', hour: 17 };
export const ACTIVITY_STOPS = [0, 25, 50, 75, 100];
/** The routes heat map's colours, solid, so the shops and the 3D blocks speak its language. */
export const ACTIVITY_RAMP = ['#4C308C', '#9A3487', '#E2483F', '#F58A3C', '#FFE0A8'];
export const ACTIVITY_UNKNOWN = '#1C1C1C';
/** A measured shop's own fill in 2D: neutral, the aura around its outline carries the value. */
export const ACTIVITY_FILL = '#2E2E2E';
/** The aura: points every ACTIVITY_STEP metres along each measured shop's outline, each glowing ACTIVITY_RADIUS metres. */
export const ACTIVITY_STEP = 2, ACTIVITY_RADIUS = 7;
/** Points along a line sum to a density of weight * intensity * radius / (3 * step), so this makes a wall's glow equal the hour's share of the shop's peak. */
export const ACTIVITY_AURA_INTENSITY = 3 * ACTIVITY_STEP / ACTIVITY_RADIUS;
/** The aura's colour stops in heat-map density. */
export const ACTIVITY_GLOW_STOPS = [0.05, 0.2, 0.4, 0.6, 0.8, 0.97];
/** A colour for the recorded percentage, preserving zero separately from absence. */
export function activityColour(percent: number | null): string {
  if (percent === null || !Number.isFinite(percent) || percent < 0 || percent > 100) return ACTIVITY_UNKNOWN;
  const index = Math.min(3, Math.floor(percent / 25)), fraction = (percent - index * 25) / 25;
  const a = ACTIVITY_RAMP[index]!, b = ACTIVITY_RAMP[index + 1]!;
  return '#' + [1, 3, 5].map(offset => Math.round(parseInt(a.slice(offset, offset + 2), 16) * (1 - fraction) + parseInt(b.slice(offset, offset + 2), 16) * fraction).toString(16).padStart(2, '0')).join('');
}

/** The business a unit's activity is recorded under. Saved observations only: a reshaped or staged tenant inherits none. */
function recordedShop(place: PulsePlace, originals: Map<string, PropertyVenue['features'][number]>, changed: Set<string>, shops: PulseShop[]) {
  const feature = place.feature, saved = originals.get(feature.id);
  const recorded = saved && !feature.parentIds?.length && !changed.has(feature.id) && saved.name === feature.name
    ? activityShop(saved.name, shops, saved.id) : null;
  return recorded && activityShop(place.name, shops, feature.id) === recorded ? recorded : null;
}
const unitsOf = (original: PropertyVenue | null) => new Map(original?.features.filter(f => f.kind === 'unit').map(f => [f.id, f]) ?? []);

export function mapActivity(places: PulsePlace[], original: PropertyVenue | null, shops: PulseShop[], moves: PulseMove[], view: PulseTrafficView): Record<string, PulseMapActivity> {
  const originals = unitsOf(original), changed = new Set(moves.map(move => move.shopId));
  return Object.fromEntries(places.map(place => {
    const shop = recordedShop(place, originals, changed, shops);
    const percent = shop?.activity?.days.find(d => d.day === view.day)?.hours.find(p => p.hour === view.hour)?.percent ?? null;
    return [place.feature.id, { placeId: shop?.placeId ?? null, business: shop?.name ?? null, percent }];
  }));
}

/** Each recorded unit's day as 24 hourly percentages (null where the hour is not recorded); unrecorded units are absent. */
export function activityCurves(places: PulsePlace[], original: PropertyVenue | null, shops: PulseShop[], moves: PulseMove[], day: FootfallDay): Record<string, Array<number | null>> {
  const originals = unitsOf(original), changed = new Set(moves.map(move => move.shopId));
  return Object.fromEntries(places.flatMap(place => {
    const hours = recordedShop(place, originals, changed, shops)?.activity?.days.find(d => d.day === day)?.hours;
    if (!hours?.length) return [];
    const at = new Map(hours.map(h => [h.hour, h.percent]));
    return [[place.feature.id, Array.from({ length: 24 }, (_, hour) => at.get(hour) ?? null)]];
  }));
}

/** A measured shop's aura: points along its outline every ACTIVITY_STEP metres, so it glows in its own shape and spills into the
 * corridor in front, carrying its day as h0..h23 in 0..1. */
export function activityAura(places: PulsePlace[], curves: Record<string, Array<number | null>>) {
  return places.flatMap(place => {
    const curve = curves[place.feature.id];
    if (!curve) return [];
    const properties = Object.fromEntries(curve.map((v, hour) => [`h${hour}`, (v ?? 0) / 100]));
    return place.feature.rings.flatMap(ring => ring.flatMap((p, i) => {
      const q = ring[(i + 1) % ring.length]!, kx = 111320 * Math.cos(p[1] * Math.PI / 180);
      const n = Math.max(1, Math.round(Math.hypot((q[0] - p[0]) * kx, (q[1] - p[1]) * 110540) / ACTIVITY_STEP));
      return Array.from({ length: n }, (_, j) => ({ type: 'Feature' as const, properties,
        geometry: { type: 'Point' as const, coordinates: [p[0] + (q[0] - p[0]) * j / n, p[1] + (q[1] - p[1]) * j / n] as [number, number] } }));
    }));
  });
}

/** The centre's day: each hour's mean over the units recorded at that hour. */
export const centreCurve = (curves: Record<string, Array<number | null>>) => Array.from({ length: 24 }, (_, hour) => {
  const values = Object.values(curves).flatMap(c => c[hour] ?? []);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
});

/** Distinct measured businesses and unknown units are separate quantities, on the displayed floor. */
export function activityCoverage(places: PulsePlace[], values: Record<string, PulseMapActivity>, levelId: string | null) {
  const visible = places.filter(p => p.feature.level === levelId);
  const measured = visible.flatMap(p => values[p.feature.id]?.percent != null && values[p.feature.id]?.placeId ? [values[p.feature.id]!.placeId!] : []);
  return { businesses: new Set(measured).size, unknownUnits: visible.filter(p => values[p.feature.id]?.percent == null).length };
}
